import { Avatar, ConfigProvider, Menu, Spin, Tooltip } from "antd"
import { MenuItemType, SubMenuType } from "antd/es/menu/interface"
import { useState } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { ROUTES } from "#constants/routes"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  RESOURCES,
  USER_ACTIONS,
  USER_ROLES,
} from "#customTypes/composedTypes/authTypes"
import { EnumValues } from "#customTypes/types"
import useHasPermission, {
  useRouteAccessChecker,
} from "#hooks/newHooks/useHasPermission"

import AdminControlsModal from "./AdminControlsModal"
import NotificationCenter from "./Notifications/NotificationCenter"

const DangerMenuItem = styled.span<{ theme: { error: string } }>`
  cursor: pointer;
  color: ${({ theme }) => theme.error};
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.error};
    opacity: 0.8;
  }
`

// Antd seems to pass down the onClick here. Required for the popover to work.
const UserAvatar = () => {
  const { currentUser } = useUserContext()

  const defaultAvatar = "https://placehold.co/28x28"

  return (
    <Flex center style={{ padding: 8 }}>
      <Avatar
        size={30}
        icon={
          <img
            style={{ width: 30, height: 30 }}
            src={currentUser?.avatar ?? defaultAvatar}
          />
        }
        style={{ userSelect: "none" }}
        shape="circle"
      />
    </Flex>
  )
}

const ADMIN_ROLES: EnumValues<typeof USER_ROLES>[] = [
  USER_ROLES.DEV,
  USER_ROLES.SUPER,
  USER_ROLES.MANAGER,
]

const UserMenu = () => {
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false)

  const theme = useThemeContext()
  const { white, black, mainColor } = theme
  const { currentUser, impersonatedUserId, isRoleOverrideActive } =
    useUserContext()
  const { onManagerLogout } = useAuthorizationContext()
  const canAccessRoute = useRouteAccessChecker()

  const canViewManagerLogout = useHasPermission(
    RESOURCES.LOGIN,
    USER_ACTIONS.MANAGER_LOGOUT
  )

  // Admin controls visibility and state
  const isRealUserAdmin =
    currentUser?.role && ADMIN_ROLES.includes(currentUser.role)
  const isInOverrideMode = !!impersonatedUserId || isRoleOverrideActive
  const adminControlsDisabledReason = impersonatedUserId
    ? "Stop impersonation first"
    : isRoleOverrideActive
      ? "Stop role override first"
      : null

  const menuItems: { key: string; label: React.ReactNode }[] = []

  if (canAccessRoute(ROUTES.PROFILE)) {
    menuItems.push({
      key: "profile",
      label: (
        <Link to={ROUTES.PROFILE} style={{ width: 100 }}>
          Profile
        </Link>
      ),
    })
  }

  if (canAccessRoute(ROUTES.SETTINGS)) {
    menuItems.push({
      key: "settings",
      label: (
        <Link to={ROUTES.SETTINGS} style={{ width: 100 }}>
          Settings
        </Link>
      ),
    })
  }

  if (canAccessRoute(ROUTES.AUTH_TEST)) {
    menuItems.push({
      key: "auth-test",
      label: (
        <Link to={ROUTES.AUTH_TEST} style={{ width: 100 }}>
          Auth Test
        </Link>
      ),
    })
  }

  if (isRealUserAdmin) {
    menuItems.unshift({
      key: "admin-controls",
      label: isInOverrideMode ? (
        <Tooltip title={adminControlsDisabledReason}>
          <span
            style={{ color: theme.error, cursor: "not-allowed", opacity: 0.6 }}
          >
            Admin Controls
          </span>
        </Tooltip>
      ) : (
        <DangerMenuItem theme={theme} onClick={() => setIsAdminModalOpen(true)}>
          Admin Controls
        </DangerMenuItem>
      ),
    })
  }

  if (canAccessRoute(ROUTES.TEST)) {
    menuItems.push({
      key: "test",
      label: <Link to={ROUTES.TEST}>Test</Link>,
    })
  }

  if (canViewManagerLogout) {
    menuItems.push({
      key: "logout",
      label: (
        <Link to={ROUTES.LOGIN} onClick={onManagerLogout}>
          Manager log out
        </Link>
      ),
    })
  }

  const items: (MenuItemType | SubMenuType)[] = [
    {
      key: "user-menu",
      label: (
        <AnimateInFlex
          animation={ANIMATION_BY_KEY.leftSm}
          // Use different keys so it reanimates when the user changes
          key={currentUser?.id || "user-menu-avatar"}
        >
          <UserAvatar />
          <span data-testid="current-user">
            {currentUser?.name ?? <Spin style={{ paddingLeft: 20 }} />}
          </span>
        </AnimateInFlex>
      ),
      children: menuItems,
    },
  ]

  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              // Text colors
              colorPrimary: white, // Main selected text color
              itemColor: black, // Main text color for whole menu
              horizontalItemSelectedColor: "transparent", // Main underline color
              itemSelectedColor: white, // Dropdown selected text color
              itemHoverColor: mainColor, // Dropdown hover text color

              // Background colors
              itemSelectedBg: mainColor, // Dropdown selected background
              itemActiveBg: "#00000011", // Dropdown background on click
              itemHoverBg: "transparent", // Dropdown background on hover
            },
            Spin: {
              colorPrimary: white,
            },
          },
        }}
      >
        <Flex center gap={8} style={{ justifyContent: "flex-end" }}>
          <NotificationCenter />
          <Menu
            items={items}
            mode="horizontal"
            selectedKeys={["user-menu"]}
            style={{ justifyContent: "flex-end" }}
            disabledOverflow
          />
        </Flex>
      </ConfigProvider>
      {isRealUserAdmin && (
        <AdminControlsModal
          open={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
        />
      )}
    </>
  )
}

export default UserMenu
